import { useState, useEffect, useCallback, ReactNode } from "react";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebouncedValue } from "@/hooks/use-debounce";
import { normalizeSearchText } from "@/lib/utils/searchUtils";

export interface SearchResult<T> {
  data: T[];
  totalCount: number;
}

export interface GenericComboboxProps<T> {
  // Value and change handler
  value?: string;
  onChange: (value: string) => void;

  // Search function - receives normalized search term
  searchFn: (params: {
    searchTerm: string;
    normalizedSearchTerm: string;
    page: number;
    pageSize: number;
  }) => Promise<SearchResult<T>>;

  // Fetch a single item by ID (for resolving display label in edit mode)
  initialFetchFn?: (id: string) => Promise<T | null>;

  // Item accessors
  getItemValue: (item: T) => string;
  getItemLabel: (item: T) => string;
  getItemSubLabel?: (item: T) => string;
  
  // Custom item renderer (optional - uses default if not provided)
  renderItem?: (item: T, isSelected: boolean) => ReactNode;

  // UI customization
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  
  // Options
  allowClear?: boolean;
  clearLabel?: string;
  disabled?: boolean;
  error?: string;
  pageSize?: number;
  
  // Styling
  className?: string;
  popoverWidth?: string;
  popoverAlign?: "start" | "center" | "end";
}

export function GenericCombobox<T>({
  value,
  onChange,
  searchFn,
  initialFetchFn,
  getItemValue,
  getItemLabel,
  getItemSubLabel,
  renderItem,
  placeholder = "Selecione...",
  searchPlaceholder = "Buscar...",
  emptyMessage = "Nenhum item encontrado.",
  loadingMessage = "Buscando...",
  allowClear = false,
  clearLabel = "Nenhum",
  disabled = false,
  error,
  pageSize = 50,
  className,
  popoverWidth = "400px",
  popoverAlign = "start",
}: GenericComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  
  const [resolvedItem, setResolvedItem] = useState<T | null>(null);
  
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Resolve initial value display (for edit mode)
  useEffect(() => {
    if (value && initialFetchFn) {
      const found = items.find((item) => getItemValue(item) === value);
      if (!found && (!resolvedItem || getItemValue(resolvedItem) !== value)) {
        initialFetchFn(value).then((item) => {
          if (item) setResolvedItem(item);
        });
      }
    }
  }, [value, initialFetchFn]);

  // Fetch items
  useEffect(() => {
    const fetchItems = async () => {
      setIsLoading(true);
      try {
        const normalizedSearch = normalizeSearchText(debouncedSearch);
        
        const result = await searchFn({
          searchTerm: debouncedSearch,
          normalizedSearchTerm: normalizedSearch,
          page,
          pageSize,
        });

        if (page === 0) {
          setItems(result.data);
        } else {
          setItems((prev) => [...prev, ...result.data]);
        }
        setTotalCount(result.totalCount);
      } catch (err) {
        console.error('Error fetching items:', err);
        setItems([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchItems();
  }, [debouncedSearch, page, pageSize, searchFn]);

  // Reset pagination when search changes
  useEffect(() => {
    setPage(0);
    setItems([]);
  }, [debouncedSearch]);

  // Handle infinite scroll
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 10;
    if (bottom && !isLoading && items.length < totalCount) {
      setPage((prev) => prev + 1);
    }
  }, [isLoading, items.length, totalCount]);

  // Find selected item (from search results or resolved initial value)
  const selectedItem = items.find((item) => getItemValue(item) === value) || 
    (resolvedItem && getItemValue(resolvedItem) === value ? resolvedItem : undefined);

  // Default item renderer
  const defaultRenderItem = (item: T, isSelected: boolean) => (
    <>
      <Check
        className={cn(
          "mr-2 h-4 w-4 flex-shrink-0",
          isSelected ? "opacity-100" : "opacity-0"
        )}
      />
      <div className="flex flex-col flex-1 min-w-0">
        <span className="font-medium truncate">{getItemLabel(item)}</span>
        {getItemSubLabel && (
          <span className="text-xs text-muted-foreground truncate">
            {getItemSubLabel(item)}
          </span>
        )}
      </div>
    </>
  );

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "w-full justify-between",
              !value && "text-muted-foreground",
              error && "border-destructive"
            )}
          >
            <span className="truncate">
              {selectedItem ? (
                <span>
                  {getItemLabel(selectedItem)}
                  {getItemSubLabel && (
                    <span className="text-xs text-muted-foreground ml-2">
                      ({getItemSubLabel(selectedItem)})
                    </span>
                  )}
                </span>
              ) : (
                placeholder
              )}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 z-[9999] bg-background pointer-events-auto"
          style={{ width: popoverWidth }}
          align={popoverAlign}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={searchPlaceholder}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList onScroll={handleScroll} className="max-h-[300px] overflow-y-auto">
              <CommandEmpty>
                {isLoading ? loadingMessage : emptyMessage}
              </CommandEmpty>
              <CommandGroup>
                {allowClear && (
                  <CommandItem
                    value="__clear__"
                    onSelect={() => {
                      onChange("");
                      setOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    <X className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{clearLabel}</span>
                  </CommandItem>
                )}
                {items.map((item) => {
                  const itemValue = getItemValue(item);
                  const isSelected = value === itemValue;
                  return (
                    <CommandItem
                      key={itemValue}
                      value={itemValue}
                      onSelect={() => {
                        onChange(isSelected ? "" : itemValue);
                        setOpen(false);
                      }}
                      className="cursor-pointer"
                    >
                      {renderItem 
                        ? renderItem(item, isSelected) 
                        : defaultRenderItem(item, isSelected)
                      }
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {isLoading && items.length > 0 && (
                <div className="flex items-center justify-center py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">Carregando...</span>
                </div>
              )}
              {!isLoading && items.length > 0 && (
                <div className="text-xs text-muted-foreground text-center py-2 border-t">
                  Mostrando {items.length} de {totalCount}
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && (
        <p className="text-sm text-destructive mt-1">{error}</p>
      )}
    </div>
  );
}
